import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { createOrder, FallbackAvailableError } from "../../services/flightBookingAPI";
import LoadingModal from "../modal/LoadingModal";
import FallbackErrorModal from "../modal/FallbackErrorModal";
import ProcessingPaymentModal from "./ProcessingPaymentModal";
import ValidationErrorModal from "../modal/ValidationErrorModal";
import FailureModal from "../modal/FailureModal";
import { toast } from "react-toastify";

function FareRules({
  onProceedToPayment,
  fareRulesData,
  fareQuoteData,
  trace_id,
  result_idx,
  userToken,
  contactDetails,
  passengers,
  rc_booking_id,
  amount,
  is_lcc = true,
  onTransactionStateChange,
  isTransactionActive,
  source
}) {
  const navigate = useNavigate();
  const razorpayInstanceRef = useRef(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [selectedRuleSegment, setSelectedRuleSegment] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fallbackError, setFallbackError] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);
  const [currentFailureMessage, setCurrentFailureMessage] = useState("");

  // Sync internal process state with parent transaction state
  // If parent transaction state becomes false, it means the process was cancelled (e.g. via back button confirmation)
  useEffect(() => {
    if (isTransactionActive === false) {
      setIsLoading(false);
      setShowProcessingModal(false);
      setIsPaymentInProgress(false);

      // Programmatically close Razorpay modal if it's open
      if (razorpayInstanceRef.current) {
        try {
          razorpayInstanceRef.current.close();
          razorpayInstanceRef.current = null;
        } catch (e) {
          console.error("Error closing Razorpay modal:", e);
        }
      }
    }
  }, [isTransactionActive]);

  // Requirement checks
  const isPanRequired = fareQuoteData?.Results?.IsPanRequiredAtBook || fareQuoteData?.Results?.IsPanRequiredAtTicket || false;
  const isPassportRequired = fareQuoteData?.Results?.IsPassportRequiredAtBook || fareQuoteData?.Results?.IsPassportRequiredAtTicket || false;

  const getPassengerTypeLabel = (paxType) => {
    switch (paxType) {
      case 1: return "Adult";
      case 2: return "Child";
      case 3: return "Infant";
      default: return "Passenger";
    }
  };

  const validatePAN = (pan) => {
    if (!pan) return { isValid: true, error: "" };
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(pan)) {
      return {
        isValid: false,
        error: "Invalid PAN format. Format: AAAAA9999A"
      };
    }
    return { isValid: true, error: "" };
  };

  const validatePassportExpiry = (expiryDate) => {
    if (!expiryDate) return { isValid: true, error: "" };
    const expiry = new Date(expiryDate);
    const today = new Date();
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(today.getMonth() + 6);

    if (expiry < today) return { isValid: false, error: "Passport has expired" };
    if (expiry < sixMonthsFromNow) return { isValid: false, error: "Passport should be valid for at least 6 months" };
    return { isValid: true, error: "" };
  };

  // Extract fare rules data
  const fareRules = fareRulesData?.FareRules || [];
  const miniFareRules = fareRulesData?.raw_response?.MiniFareRules || [];

  // Get current rule
  const currentRule = fareRules[selectedRuleSegment];
  const currentMiniFareRules = miniFareRules[selectedRuleSegment] || [];

  // Enhanced parser for MiniFareRules (LCC)
  const parseMiniFareRules = (miniFareRulesArray) => {
    if (!miniFareRulesArray || miniFareRulesArray.length === 0) {
      return { cancellation: [], reissue: [] };
    }

    const cancellationRules = [];
    const reissueRules = [];

    miniFareRulesArray.forEach((rule) => {
      const ruleData = {
        ruleType: rule.Type === "Cancellation" ? "Cancellation" : "Reissue",
        timeframe:
          rule.From && rule.To
            ? `${rule.From} - ${rule.To} ${rule.Unit || ""}`
            : rule.From
              ? `${rule.From}+ ${rule.Unit || ""}`
              : "Anytime",
        description: `${rule.JourneyPoints || ""}`,
        allowed:
          rule.Details &&
          !rule.Details.toLowerCase().includes("not permitted") &&
          !rule.Details.toLowerCase().includes("non-refundable"),
        penalty: rule.Details || "-",
      };

      if (rule.Type === "Cancellation") {
        cancellationRules.push(ruleData);
      } else if (rule.Type === "Reissue") {
        reissueRules.push(ruleData);
      }
    });

    return { cancellation: cancellationRules, reissue: reissueRules };
  };

  // Enhanced parser for non-LCC HTML fare rules
  const parseNonLCCFareRules = (fareRuleDetail) => {
    if (!fareRuleDetail) return { cancellation: [], reissue: [] };

    const cancellationRules = [];
    const reissueRules = [];

    // Extract text content for parsing
    const textContent = fareRuleDetail.replace(/<[^>]*>/g, "\n");

    // Parse CANCELLATION rules
    const cancellationMatch = textContent.match(
      /CANCELLATIONS?\s+(.*?)(?=CHANGES|REISSUE|Category-|$)/is
    );
    if (cancellationMatch) {
      const cancellationText = cancellationMatch[1];

      // Extract charge amounts
      const chargeMatches = [
        ...cancellationText.matchAll(
          /CHARGE\s+INR\s+([\d,]+)\s+(?:FOR\s+)?(CANCEL|PER)/gi
        ),
      ];

      chargeMatches.forEach((match, index) => {
        const amount = match[1];

        // Determine timeframe based on context
        let timeframe = "";
        let description = "";

        if (cancellationText.includes("BEFORE 24 HOURS") && index === 0) {
          timeframe = "Prior To Departure";
          description = "Refund permitted before 24 hours of departure";
        } else if (
          cancellationText.includes("WITHIN 24 HRS") ||
          cancellationText.includes("UNTIL 02 HR")
        ) {
          timeframe = "Prior To Departure";
          description =
            "Refund permitted within 24 hrs until 2 hrs before departure";
        } else if (cancellationText.includes("NO-SHOW")) {
          timeframe = "No Show";
          description = "Refund not permitted after departure no show";
        } else if (cancellationText.includes("AFTER DEPARTURE")) {
          timeframe = "After Departure";
          description = "Refund not permitted after departure";
        }

        cancellationRules.push({
          ruleType: "Cancellation",
          timeframe: timeframe || "Anytime",
          description: description || "Cancellation permitted",
          allowed:
            !cancellationText.toLowerCase().includes("non-refundable") &&
            !cancellationText.toLowerCase().includes("not permitted"),
          penalty: `INR ${amount}`,
        });
      });

      // Handle NO-SHOW cases
      if (cancellationText.includes("NON-REFUNDABLE IN CASE OF NO-SHOW")) {
        cancellationRules.push({
          ruleType: "Cancellation",
          timeframe: "No Show",
          description: "Refund not permitted before departure no show",
          allowed: false,
          penalty: "-",
        });
      }
    }

    // Parse REISSUE/CHANGES rules
    const changesMatch = textContent.match(
      /CHANGES?\s+(.*?)(?=CANCELLATION|Category-|$)/is
    );
    if (changesMatch) {
      const changesText = changesMatch[1];

      const chargeMatches = [
        ...changesText.matchAll(
          /CHARGE\s+INR\s+([\d,]+)\s+(?:FOR\s+)?(REISSUE|PER)/gi
        ),
      ];

      chargeMatches.forEach((match, index) => {
        const amount = match[1];

        let timeframe = "";
        let description = "";

        if (changesText.includes("BEFORE 24 HOURS") && index === 0) {
          timeframe = "Prior To Departure";
          description = "Change permitted before 24 hours of departure";
        } else if (
          changesText.includes("WITHIN 24") ||
          changesText.includes("UNTIL 02 HR")
        ) {
          timeframe = "Prior To Departure";
          description = "Reissue permitted before departure";
        } else if (changesText.includes("NO-SHOW")) {
          timeframe = "No Show";
          description = "Change not permitted before departure no show";
        } else if (changesText.includes("AFTER DEPARTURE")) {
          timeframe = "After Departure";
          description = "Reissue not permitted after departure";
        }

        reissueRules.push({
          ruleType: "Reissue",
          timeframe: timeframe || "Anytime",
          description: description || "Reissue permitted",
          allowed:
            amount === "0" ||
            !changesText.toLowerCase().includes("not permitted"),
          penalty: amount === "0" ? "INR 0" : `INR ${amount}`,
        });
      });

      // Handle NO-SHOW for reissue
      if (changesText.includes("NO-SHOW")) {
        reissueRules.push({
          ruleType: "Reissue",
          timeframe: "No Show",
          description: "Reissue permitted before departure no show",
          allowed: true,
          penalty: "INR 0",
        });
      }
    }

    // Add default rules if none found
    if (cancellationRules.length === 0) {
      cancellationRules.push(
        {
          ruleType: "Cancellation",
          timeframe: "Prior To Departure",
          description: "Refund not permitted before departure",
          allowed: false,
          penalty: "-",
        },
        {
          ruleType: "Cancellation",
          timeframe: "No Show",
          description: "Refund not permitted before departure no show",
          allowed: false,
          penalty: "-",
        },
        {
          ruleType: "Cancellation",
          timeframe: "After Departure",
          description: "Refund not permitted after departure",
          allowed: false,
          penalty: "-",
        }
      );
    }

    if (reissueRules.length === 0) {
      reissueRules.push(
        {
          ruleType: "Reissue",
          timeframe: "Prior To Departure",
          description: "Change not permitted before departure",
          allowed: false,
          penalty: "-",
        },
        {
          ruleType: "Reissue",
          timeframe: "Prior To Departure",
          description: "Reissue permitted before departure",
          allowed: true,
          penalty: "INR 0",
        },
        {
          ruleType: "Reissue",
          timeframe: "No Show",
          description: "Change not permitted before departure no show",
          allowed: false,
          penalty: "-",
        }
      );
    }

    return { cancellation: cancellationRules, reissue: reissueRules };
  };

  // Determine which parser to use
  const parsedMiniFareRules = parseMiniFareRules(currentMiniFareRules);
  const parsedNonLCCRules = parseNonLCCFareRules(currentRule?.FareRuleDetail);

  // Use LCC rules if available, otherwise use non-LCC rules
  const finalRules =
    parsedMiniFareRules.cancellation.length > 0 ||
      parsedMiniFareRules.reissue.length > 0
      ? parsedMiniFareRules
      : parsedNonLCCRules;

  // HTML sanitization helper
  const sanitizeHTML = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };

  // Extract additional terms from HTML
  const getAdditionalTerms = () => {
    if (!currentRule?.FareRuleDetail) return null;

    const ulStart = currentRule.FareRuleDetail.indexOf("<ul>");
    const ulEnd = currentRule.FareRuleDetail.lastIndexOf("</ul>");

    if (ulStart !== -1 && ulEnd !== -1) {
      return currentRule.FareRuleDetail.substring(ulStart, ulEnd + 5);
    }

    return null;
  };

  // Razorpay helper functions
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const navigateToStatusPage = (rc_booking_id_param, amount_param) => {
    // Store booking ID and contact info for recovery on reload
    if (rc_booking_id_param) {
      localStorage.setItem("rc_booking_id", rc_booking_id_param);
    }
    if (contactDetails?.email) {
      localStorage.setItem("userEmail", contactDetails.email);
    }
    if (contactDetails?.phone) {
      localStorage.setItem("userPhone", contactDetails.phone);
    }

    const urlParams = new URLSearchParams({
      rc_booking_id: rc_booking_id_param || "",
      trace_id: trace_id || "",
      result_idx: result_idx || "",
    }).toString();

    const navState = {
      rc_booking_id: rc_booking_id_param,
      trace_id,
      result_idx,
      userToken,
      amount: amount_param,
      contactDetails,
      source
    };

    // console.log("[FareRules] Navigating to status page with:", navState);
    navigate(`/booking-flight?${urlParams}`, {
      state: navState,
    });
  };

  const openRazorpayCheckout = async (paymentData) => {
    const { rzp_order_id, rzp_key_id, rc_booking_id: booking_id, amount: payAmount, currency } = paymentData;

    if (!rzp_order_id || !rzp_key_id) {
      console.error("[FareRules] Missing order ID or Key ID", paymentData);
      const errMsg = "Payment initialization failed. Please try again.";
      setError(errMsg);
      setCurrentFailureMessage(errMsg);
      setIsPaymentInProgress(false);
      setIsFailureModalOpen(true);
      return;
    }

    setShowProcessingModal(true);

    const res = await loadRazorpayScript();

    if (!res) {
      const errMsg = "Razorpay SDK failed to load. Are you online?";
      setError(errMsg);
      setCurrentFailureMessage(errMsg);
      setShowProcessingModal(false);
      setIsPaymentInProgress(false);
      setIsFailureModalOpen(true);
      return;
    }

    const options = {
      key: rzp_key_id,
      amount: Math.round(payAmount * 100),
      currency: currency || "INR",
      name: "Route Chef",
      description: "Flight Booking Checkout",
      order_id: rzp_order_id,
      handler: async function (response) {
        razorpayInstanceRef.current = null; // Clear ref on success
        // console.log("[Razorpay] Success Handler:", response);

        // Payment status update is handled by backend webhook (pg_event_handler)
        // The webhook receives the payment event from Razorpay and triggers
        // the ticketing flow via TBO lambda - no frontend DB update needed

        // Navigate to status page where polling will pick up the backend updates
        onTransactionStateChange?.(false);
        navigateToStatusPage(booking_id, payAmount);
      },
      prefill: {
        name: "",
        email: contactDetails?.email || "",
        contact: contactDetails?.phone || "",
      },
      theme: {
        color: "#F97316", // Orange-500
      },
      modal: {
        ondismiss: function () {
          // console.log("[Razorpay] Modal dismissed by user");
          setShowProcessingModal(false);
          setIsPaymentInProgress(false); // Allow retry
          setIsLoading(false);
          // User closed the modal without completing payment
          // Don't navigate - let them retry or go back
          toast.error("Payment was cancelled. Please try again when ready.", { position: "top-center" });
          setError("Payment was cancelled. Please try again when ready.");
          onTransactionStateChange?.(false);
          razorpayInstanceRef.current = null; // Clear ref on dismiss
        }
      }
    };

    const rzp1 = new window.Razorpay(options);
    razorpayInstanceRef.current = rzp1;
    rzp1.on('payment.failed', function (response) {
      console.error("[Razorpay] Payment Failed:", response.error);
      const errMsg = `Payment Failed: ${response.error.description}`;
      setError(errMsg);
      setCurrentFailureMessage(errMsg);
      setShowProcessingModal(false);
      setIsPaymentInProgress(false); // Allow retry
      setIsLoading(false);
      onTransactionStateChange?.(false);
      setIsFailureModalOpen(true);
    });

    setShowProcessingModal(false);
    rzp1.open();
  };

  // Handler functions - defined before early return to avoid initialization errors
  const handleProceedToPayment = async () => {
    if (!acceptedTerms) {
      toast.error("Please accept the terms and conditions to proceed.", { position: "top-center" });
      setError("Please accept the terms and conditions to proceed.");
      return;
    }

    // Comprehensive Validation
    const errors = [];

    // 1. Contact Details Validation
    if (!contactDetails?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactDetails.email)) {
      errors.push("A valid contact email is required for the Lead Traveller.");
    }
    if (!contactDetails?.phone || contactDetails.phone.length < 10) {
      errors.push("A valid 10-digit mobile number is required for the Lead Traveller.");
    }

    // 2. Passenger Validation
    if (!passengers || passengers.length === 0) {
      errors.push("Passenger details are missing. Please wait for the form to load or refresh.");
    } else {
      passengers.forEach((p, idx) => {
        const typeLabel = getPassengerTypeLabel(p.PaxType);
        const pNum = idx + 1;
        const pIdentifier = `${typeLabel} ${pNum}`;

        if (!p.FirstName?.trim()) {
          errors.push(`${pIdentifier}: First name is required.`);
        }
        if (!p.LastName?.trim()) {
          errors.push(`${pIdentifier}: Last name is required.`);
        }
        if (!p.DateOfBirth) {
          errors.push(`${pIdentifier}: Date of birth is required.`);
        }

        // Validate individual passenger contact info if it's the lead passenger (first adult)
        if (p.IsLeadPax) {
          if (!p.Email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.Email)) {
            errors.push(`${pIdentifier} (Lead): A valid contact email is required.`);
          }
          if (!p.ContactNo?.trim() || p.ContactNo.length < 10) {
            errors.push(`${pIdentifier} (Lead): A valid mobile number is required.`);
          }
        }

        // PAN validation
        if (isPanRequired && p.PaxType === 1) {
          if (!p.PAN) {
            errors.push(`${pIdentifier}: PAN number is required.`);
          } else {
            const panVal = validatePAN(p.PAN);
            if (!panVal.isValid) errors.push(`${pIdentifier}: ${panVal.error}`);
          }
        }

        // Passport validation
        if (isPassportRequired) {
          if (!p.PassportNo) {
            errors.push(`${pIdentifier}: Passport number is required.`);
          }
          if (!p.PassportExpiry) {
            errors.push(`${pIdentifier}: Passport expiry date is required.`);
          } else {
            const passVal = validatePassportExpiry(p.PassportExpiry);
            if (!passVal.isValid) errors.push(`${pIdentifier}: ${passVal.error}`);
          }
        }
      });

      // 3. Duplicate Name Check
      const nameMap = new Map();
      passengers.forEach((p, idx) => {
        const fName = p.FirstName?.trim().toLowerCase();
        const lName = p.LastName?.trim().toLowerCase();
        if (fName && lName) {
          const fullName = `${fName} ${lName}`;
          if (nameMap.has(fullName)) {
            const firstIdx = nameMap.get(fullName);
            const typeLabel = getPassengerTypeLabel(p.PaxType);
            const firstTypeLabel = getPassengerTypeLabel(passengers[firstIdx].PaxType);
            errors.push(`Duplicate Name: ${firstTypeLabel} ${firstIdx + 1} and ${typeLabel} ${idx + 1} have the same name (${p.FirstName} ${p.LastName}). All passengers must have unique names as per airline policy.`);
          } else {
            nameMap.set(fullName, idx);
          }
        }
      });
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    setError(null);
    setIsLoading(true);
    onTransactionStateChange?.(true);

    try {
      const leadEmail = contactDetails?.email || "";
      const countryCode = contactDetails?.countryCode || "+91";
      const phoneNum = contactDetails?.phone || "";
      const fullPhoneRaw = phoneNum.startsWith("+") ? phoneNum : `${countryCode}${phoneNum}`;
      // Ensure numeric format (cast to Number)
      const numericPhone = Number(fullPhoneRaw.replace(/\D/g, ""));
      const userContact = leadEmail || numericPhone || "";

      // Attach fare data to each passenger (required by TBO Ticket API)
      const fareBreakdown = fareQuoteData?.Results?.FareBreakdown || [];
      ;

      const passengersWithFare = passengers.map(passenger => {
        // Find matching fare breakdown by PaxType (1=Adult, 2=Child, 3=Infant)
        const matchingFare = fareBreakdown.find(fb => fb.PassengerType === passenger.PaxType);

        let passengerData = {
          ...passenger,
          Email: leadEmail,
          ContactNo: numericPhone, // Send as Number type
        };

        if (matchingFare) {
          passengerData.Fare = {
            Currency: matchingFare.Currency || "INR",
            BaseFare: matchingFare.BaseFare,
            Tax: matchingFare.Tax,
            YQTax: matchingFare.YQTax || 0,
            AdditionalTxnFeeOfrd: matchingFare.AdditionalTxnFeeOfrd || 0,
            AdditionalTxnFeePub: matchingFare.AdditionalTxnFeePub || 0,
            PGCharge: matchingFare.PGCharge || 0
          };
        }
        return passengerData;
      });

      // console.log("[FareRules] Passengers with Fare:", passengersWithFare);
      // console.log("[FareRules] is_lcc:", is_lcc);

      // Call createOrder API with passengers (including fare data)
      const orderResponse = await createOrder(
        rc_booking_id,
        trace_id,
        result_idx,
        amount,
        userToken,
        userContact,
        numericPhone,
        contactDetails?.email,
        passengersWithFare,
        is_lcc
      );

      // console.log("[FareRules] Order Response Data:", orderResponse?.data);
      // console.log("[FareRules] Amount from order:", orderResponse?.data?.amount);
      // console.log("[FareRu  les] Amount from props:", amount);

      // Extract payment data
      const paymentData = {
        rc_booking_id: orderResponse?.data?.rc_booking_id || orderResponse?.rc_booking_id || rc_booking_id,
        rzp_order_id: orderResponse?.data?.rzp_order_id,
        rzp_key_id: orderResponse?.data?.rzp_key_id,
        amount: orderResponse?.data?.amount || amount,
        currency: orderResponse?.data?.currency || "INR",
        status: orderResponse?.data?.status,
      };

      // console.log("[FareRules] Sending to Razorpay:", paymentData.amount);

      // Directly open Razorpay checkout instead of navigating to PaymentMethod
      setIsPaymentInProgress(true);
      await openRazorpayCheckout(paymentData);
      setIsLoading(false);

    } catch (err) {
      console.error("[FareRules] Error creating order:", err);
      setIsLoading(false);

      // Check if this is a fallback-available error
      if (err instanceof FallbackAvailableError) {
        // console.log("[FareRules] Fallback data available, showing modal");
        setFallbackError(err);
      } else {
        toast.error(err.message || "Failed to create order. Please try again.", { position: "top-center" });
        setError(err.message || "Failed to create order. Please try again.");
      }
      onTransactionStateChange?.(false);
    }
  };

  // Fallback Modal Handlers
  const handleContinueWithMock = () => {
    // console.log("[FareRules] User chose to continue with mock data");
    if (fallbackError && fallbackError.fallbackData) {
      // Use the mock order data to proceed
      const mockOrder = fallbackError.fallbackData;
      const paymentData = {
        rc_booking_id: mockOrder?.data?.rc_booking_id || mockOrder?.rc_booking_id || rc_booking_id,
        rzp_order_id: mockOrder?.data?.rzp_order_id,
        rzp_key_id: mockOrder?.data?.rzp_key_id,
        amount: mockOrder?.data?.amount || amount,
        currency: mockOrder?.data?.currency || "INR",
        status: mockOrder?.data?.status,
      };

      // console.log("[FareRules] Using mock payment data:", paymentData);

      // Directly open Razorpay checkout with mock data
      setIsPaymentInProgress(true);
      openRazorpayCheckout(paymentData);
      setIsLoading(false);
    }
    setFallbackError(null);
  };

  const handleTryAgainFallback = () => {
    // console.log("[FareRules] User chose to try again");
    setFallbackError(null);
    // Retry the payment order creation
    handleProceedToPayment();
  };

  const handleGoBackFallback = () => {
    // console.log("[FareRules] User chose to go back");
    setFallbackError(null);
    navigate("/chat");
  };

  // If no fare rules available
  if (fareRules.length === 0) {
    return (
      <>
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 md:pt-0 md:pl-0 md:pr-0">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6 bg-[#FFF6EC] p-4 md:p-6 min-h-[60px]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 2V5M7.5 2V5M12 2V5" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 3.5H11C7.70017 3.5 6.05025 3.5 5.02513 4.52513C4 5.55025 4 7.20017 4 10.5V15C4 18.2998 4 19.9497 5.02513 20.9749C6.05025 22 7.70017 22 11 22H13C16.2998 22 17.9497 22 18.9749 20.9749C20 19.9497 20 18.2998 20 15V10.5C20 7.20017 20 5.55025 18.9749 4.52512C17.9497 3.5 16.2998 3.5 13 3.5Z" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 15H12M8 11H16" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            <h2 className="text-lg md:text-xl font-semibold text-orange-500">
              Fare Rules & Policies
            </h2>
          </div>

          {/* Empty State Content */}
          <div className="flex flex-col items-center justify-center py-12 px-4 space-y-6">
            {/* Icon */}
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#FF6B35" fillOpacity="0.2" />
                <path d="M12 7V13M12 15V17" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="9" stroke="#FF6B35" strokeWidth="2" />
              </svg>
            </div>

            {/* Message */}
            <div className="text-center space-y-2 max-w-md">
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                No Fare Rules Available
              </h3>
              <p className="text-sm md:text-base text-gray-600">
                Detailed fare rules are not available for this flight at the moment. Please contact support if you need specific information about cancellation or change policies.
              </p>
            </div>


          </div>

          {/* Bottom Actions - Checkbox and Button */}
          <div className="space-y-4 px-4 pb-4">
            {/* Terms Acceptance */}
            <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div
                className={`relative flex items-center justify-center w-5 h-5 border-2 rounded-md cursor-pointer transition-all duration-200 mt-0.5 flex-shrink-0 ${acceptedTerms ? 'bg-[#FF4F01] border-[#FF4F01]' : 'bg-white border-orange-300 hover:border-[#FF4F01]'}`}
                onClick={() => setAcceptedTerms(!acceptedTerms)}
              >
                {acceptedTerms && (
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <label
                htmlFor="accept-terms-empty"
                className="text-sm md:text-base cursor-pointer text-gray-900 select-none"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
              >
                I understand and confirm that I have read all the points and agree to proceed without detailed fare rules.
              </label>
            </div>

            {/* Proceed Button */}
            <button
              onClick={handleProceedToPayment}
              className={`w-full md:w-auto px-8 py-3 rounded-lg font-medium text-white transition-colors ${acceptedTerms
                ? "bg-gray-800 hover:bg-gray-900"
                : "bg-gray-400 cursor-not-allowed"
                }`}
              disabled={!acceptedTerms}
            >
              I Understand, Proceed to Payment
            </button>
          </div>
        </div>

        {/* Loading Modal */}
        <LoadingModal
          isOpen={isLoading}
          title="Creating Your Order"
          message="Please wait while we process your booking request. This will only take a moment."
          showWarning={true}
          warningText="Please do not refresh this page or use the back button while your order is being created."
        />

        {/* Fallback Error Modal */}
        <FallbackErrorModal
          isOpen={!!fallbackError}
          operationName={fallbackError?.operationName || ""}
          errorMessage={fallbackError?.message || ""}
          errorDetails={fallbackError?.errorDetails}
          onContinueWithMock={handleContinueWithMock}
          onTryAgain={handleTryAgainFallback}
          onGoBack={handleGoBackFallback}
        />

        {/* Validation Error Modal */}
        <ValidationErrorModal
          isOpen={isValidationModalOpen}
          errors={validationErrors}
          onClose={() => setIsValidationModalOpen(false)}
        />

        {/* Payment Failure Modal */}
        {isFailureModalOpen && (
          <FailureModal
            onRetry={() => {
              setIsFailureModalOpen(false);
              handleProceedToPayment();
            }}
            onContactSupport={() => {
              setIsFailureModalOpen(false);
              // Find the closest way to open contact modal or redirect
              window.dispatchEvent(new CustomEvent('open-contact-support'));
            }}
            errorMessage={currentFailureMessage}
          />
        )}
      </>
    );
  }

  const additionalTerms = getAdditionalTerms();

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 md:pt-0 md:pl-0 md:pr-0 ">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-800 font-medium">
                  Error: {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 flex-shrink-0"
              >
                <svg
                  className="h-5 w-5 text-red-500 hover:text-red-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-6 bg-[#FFF6EC] p-4 md:p-6 min-h-[60px]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 2V5M7.5 2V5M12 2V5" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M13 3.5H11C7.70017 3.5 6.05025 3.5 5.02513 4.52513C4 5.55025 4 7.20017 4 10.5V15C4 18.2998 4 19.9497 5.02513 20.9749C6.05025 22 7.70017 22 11 22H13C16.2998 22 17.9497 22 18.9749 20.9749C20 19.9497 20 18.2998 20 15V10.5C20 7.20017 20 5.55025 18.9749 4.52512C17.9497 3.5 16.2998 3.5 13 3.5Z" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 15H12M8 11H16" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          <h2 className="text-lg md:text-xl font-semibold text-orange-500">
            Fare Rules & Policies
          </h2>
        </div>

        <div className="space-y-6 pl-4 pr-4 pt-4">
          {/* Multi-Segment Tabs */}
          {fareRules.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {fareRules.map((rule, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedRuleSegment(index)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${selectedRuleSegment === index
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {rule.Origin} → {rule.Destination}
                </button>
              ))}
            </div>
          )}

          {/* Fare Basis Code */}
          {currentRule?.FareBasisCode && (
            <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs md:text-sm text-gray-600">
                  Fare Basis Code:
                </span>
                <span className="text-xs md:text-sm font-semibold text-gray-900">
                  {currentRule.FareBasisCode}
                </span>
              </div>
            </div>
          )}

          {/* Cancellation Fare Rules */}
          {finalRules.cancellation.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 text-center">
                Cancellation Fare Rules
              </h3>

              {/* Mobile View - Cards */}
              <div className="block md:hidden space-y-3">
                {finalRules.cancellation.map((rule, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-sm text-gray-900">
                        {rule.timeframe}
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${rule.allowed
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {rule.allowed ? "true" : "false"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      {rule.description}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {rule.penalty}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop/Tablet View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Rule Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Allowed Modification
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Penalty Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {finalRules.cancellation.map((rule, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {rule.timeframe}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {rule.description}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${rule.allowed
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}
                          >
                            {rule.allowed ? "true" : "false"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900 font-medium">
                          {rule.penalty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reissue Fare Rules */}
          {finalRules.reissue.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 text-center">
                Reissue Fare Rules
              </h3>

              {/* Mobile View - Cards */}
              <div className="block md:hidden space-y-3">
                {finalRules.reissue.map((rule, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-sm text-gray-900">
                        {rule.timeframe}
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${rule.allowed
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {rule.allowed ? "true" : "false"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      {rule.description}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {rule.penalty}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop/Tablet View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Rule Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Allowed Modification
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Penalty Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {finalRules.reissue.map((rule, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {rule.timeframe}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {rule.description}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${rule.allowed
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}
                          >
                            {rule.allowed ? "true" : "false"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900 font-medium">
                          {rule.penalty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Additional Terms & Conditions */}
          {additionalTerms && (
            <div className="space-y-2">
              <h3 className="text-sm md:text-base font-semibold text-gray-900">
                Important Information
              </h3>
              <div
                className="text-xs md:text-sm text-gray-700 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200"
                dangerouslySetInnerHTML={sanitizeHTML(additionalTerms)}
                style={{
                  lineHeight: "1.6",
                }}
              />
            </div>
          )}

          {/* No Rules Available */}
          {finalRules.cancellation.length === 0 &&
            finalRules.reissue.length === 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    Detailed fare rules not available for this segment.
                  </span>
                </div>
              </div>
            )}

          {/* Terms Acceptance */}
          <div className="flex items-start gap-3 mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div
              className={`relative flex items-center justify-center w-5 h-5 border-2 rounded-md cursor-pointer transition-all duration-200 mt-0.5 flex-shrink-0 ${acceptedTerms ? 'bg-[#FF4F01] border-[#FF4F01]' : 'bg-white border-orange-300 hover:border-[#FF4F01]'}`}
              onClick={() => setAcceptedTerms(!acceptedTerms)}
            >
              {acceptedTerms && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <label
              htmlFor="accept-terms"
              className="text-sm md:text-base cursor-pointer text-gray-900 select-none"
              onClick={() => setAcceptedTerms(!acceptedTerms)}
            >
              I understand and confirm that I have read all the points.
            </label>
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceedToPayment}
            className={`w-full md:w-auto px-8 py-3 rounded-lg font-medium text-white transition-colors ${acceptedTerms
              ? "bg-gray-800 hover:bg-gray-900"
              : "bg-gray-400 cursor-not-allowed"
              }`}
            disabled={!acceptedTerms}
          >
            I Understand, Proceed to Payment
          </button>
        </div>
      </div>

      {/* Loading Modal */}
      <LoadingModal
        isOpen={isLoading}
        title="Creating Your Order"
        message="Please wait while we process your booking request. This will only take a moment."
        showWarning={true}
        warningText="Please do not refresh this page or use the back button while your order is being created."
      />

      {/* Fallback Error Modal */}
      <FallbackErrorModal
        isOpen={!!fallbackError}
        operationName={fallbackError?.operationName || ""}
        errorMessage={fallbackError?.message || ""}
        errorDetails={fallbackError?.errorDetails}
        onContinueWithMock={handleContinueWithMock}
        onTryAgain={handleTryAgainFallback}
        onGoBack={handleGoBackFallback}
      />

      {/* Processing Payment Modal */}
      <ProcessingPaymentModal isOpen={showProcessingModal} />

      {/* Validation Error Modal */}
      <ValidationErrorModal
        isOpen={isValidationModalOpen}
        errors={validationErrors}
        onClose={() => setIsValidationModalOpen(false)}
      />

      {/* Payment Failure Modal */}
      {isFailureModalOpen && (
        <FailureModal
          onRetry={() => {
            setIsFailureModalOpen(false);
            handleProceedToPayment();
          }}
          onContactSupport={() => {
            setIsFailureModalOpen(false);
            // Fire event or let parent handle, BookFlight listens to contact modal usually
            window.dispatchEvent(new CustomEvent('open-contact-support'));
          }}
          errorMessage={currentFailureMessage}
        />
      )}
    </>
  );
}

FareRules.propTypes = {
  onProceedToPayment: PropTypes.func,
  fareRulesData: PropTypes.object,
  fareQuoteData: PropTypes.object,
  trace_id: PropTypes.string,
  result_idx: PropTypes.string,
  userToken: PropTypes.string,
  contactDetails: PropTypes.object,
  passengers: PropTypes.array,
  rc_booking_id: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  is_lcc: PropTypes.bool,
};

export default FareRules;
